import { TCountryCode } from 'interfaces';
import {
  ISearchQuery,
  HotelResult,
  ErrorResponse,
  MealPlanNames,
  StarRating,
  SearchOptions,
  NameSearchResponseData,
} from 'services/BackendApi';
import { ESortOrder } from 'store/common/types';
import { getDefaultSearchAndBookingStartDate, getDefaultSearchAndBookingEndDate } from '../../utils';
import qs from 'qs';

export interface FastSearchDomain {
  options: SearchOptions | null; // We should look at using fp-ts to make use of `Option` types
  showRegions: boolean;
  showDatePicker: boolean;
  showLodgingControls: boolean;
  showNameSearchResults: boolean;
  activeLodgingIndex: number;
  expandedHighlights: string[];
  datePickerCurrentDate: string;
  dateSelectionInProgress: boolean;
  anchorDate?: string;

  offersRequestPending: boolean;
  offersRequestError: ErrorResponse | null;
  nameSearchPending: boolean;
  nameSearchRequestError: ErrorResponse | null;

  results: HotelResult[] | null;
  nameSearchResults: NameSearchResponseData | undefined;

  optionsRequestPending: boolean;
  optionsRequestError: ErrorResponse | null;

  query: ISearchQuery;
  originalQuery: ISearchQuery | null;

  hotelUuid: string | null;
  queryHasChanged: boolean;
  activeHotelId: string | undefined;
  sortOrder: ESortOrder;
}

// Parse full URL query string to initialize search state with URL values
const queryString = window.location.search;
const urlQueryParams = qs.parse(queryString.slice(1)); // Remove leading '?'

// Create default query values
const defaultQuery: ISearchQuery = {
  name: '',
  lodgings: [
    {
      numberOfAdults: 2,
      agesOfAllChildren: [],
      repeatCustomer: false,
    },
  ],
  mealPlanCategories: ['Any' as MealPlanNames],
  regions: [],
  filters: [],
  starRatings: [],
  startDate: getDefaultSearchAndBookingStartDate(),
  endDate: getDefaultSearchAndBookingEndDate(),
  priceRange: { min: undefined, max: undefined },
  clientCountryCode: null,
};

// Merge URL params with defaults - URL params take precedence where they exist
const initialQuery: ISearchQuery = {
  ...defaultQuery,
  ...(urlQueryParams.name ? { name: urlQueryParams.name as string } : {}),
  ...(urlQueryParams.clientCountryCode ? { clientCountryCode: urlQueryParams.clientCountryCode as TCountryCode } : {}),
  ...(urlQueryParams.startDate ? { startDate: urlQueryParams.startDate as string } : {}),
  ...(urlQueryParams.endDate ? { endDate: urlQueryParams.endDate as string } : {}),
  ...(urlQueryParams.lodgings ? { 
    lodgings: Array.isArray(urlQueryParams.lodgings) 
      ? (urlQueryParams.lodgings as any[]).map(lodging => ({
          ...lodging,
          numberOfAdults: parseInt(lodging.numberOfAdults) || 2,
          agesOfAllChildren: Array.isArray(lodging.agesOfAllChildren) 
            ? lodging.agesOfAllChildren.map(age => parseInt(age) || 0)
            : [],
          repeatCustomer: lodging.repeatCustomer === 'true' || lodging.repeatCustomer === true
        }))
      : [{
          ...urlQueryParams.lodgings,
          numberOfAdults: parseInt((urlQueryParams.lodgings as any).numberOfAdults) || 2,
          agesOfAllChildren: Array.isArray((urlQueryParams.lodgings as any).agesOfAllChildren) 
            ? (urlQueryParams.lodgings as any).agesOfAllChildren.map(age => parseInt(age) || 0)
            : [],
          repeatCustomer: (urlQueryParams.lodgings as any).repeatCustomer === 'true' || (urlQueryParams.lodgings as any).repeatCustomer === true
        }]
  } : {}),
  ...(urlQueryParams.mealPlanCategories ? { mealPlanCategories: Array.isArray(urlQueryParams.mealPlanCategories) ? urlQueryParams.mealPlanCategories as MealPlanNames[] : [urlQueryParams.mealPlanCategories as MealPlanNames] } : {}),
  ...(urlQueryParams.regions ? { regions: Array.isArray(urlQueryParams.regions) ? urlQueryParams.regions : [urlQueryParams.regions] } : {}),
  ...(urlQueryParams.filters ? { filters: Array.isArray(urlQueryParams.filters) ? urlQueryParams.filters : [urlQueryParams.filters] } : {}),
  ...(urlQueryParams.starRatings ? { starRatings: Array.isArray(urlQueryParams.starRatings) ? urlQueryParams.starRatings as StarRating[] : [urlQueryParams.starRatings as StarRating] } : {}),
  ...(urlQueryParams.priceRange ? { priceRange: urlQueryParams.priceRange as any } : {}),
};

export const initialState: FastSearchDomain = {
  activeHotelId: undefined,
  results: null,
  nameSearchResults: undefined,
  options: null,
  showDatePicker: false,
  showRegions: false,
  showLodgingControls: false,
  showNameSearchResults: false,
  optionsRequestPending: true,
  offersRequestPending: false,
  offersRequestError: null,
  nameSearchPending: false,
  optionsRequestError: null,
  nameSearchRequestError: null,
  activeLodgingIndex: 0,
  expandedHighlights: [],
  sortOrder: ESortOrder.ASC,

  datePickerCurrentDate: new Date().toISOString(),
  dateSelectionInProgress: false,

  queryHasChanged: false,
  query: initialQuery,
  originalQuery: null,
  hotelUuid: null,
};
